<script setup lang="ts">
import iconFileUnknown from "~/assets/icons/file/unknown.svg?url"

const props = defineProps({
  transferId: null
})
const { copyJob } = useJobs()
const { clearNotificationById } = useNotificationTray()
const copy = copyJob(props.transferId)

if (!isDefined(copy))
  throw new Error(`Copy job with transferId ${props.transferId} not found`)

const job = copy.state
const totalFiles = copy.totalFiles
const totalBytes = copy.totalBytes
const failedBytes = copy.failedBytes
const completedBytes = copy.completedBytes
const finishedBytes = copy.finishedBytes
</script>

<template>
  <div class="container">
    <div class="preview">
      <img :src="iconFileUnknown" class="preview-icon">
    </div>
    <div class="content">
      <div class="info">
        <div class="info-left">
          <span v-if="job.title">
            {{ job.title }}
          </span>
          <span v-else-if="job.status === 'complete'">
            Copy complete
          </span>
          <span v-else-if="job.status === 'preparing'">
            Allocating...
          </span>
          <span v-else-if="totalFiles === 1">
            Copying file...
          </span>
          <span v-else>
            Copying {{ totalFiles }} files...
          </span>
        </div>
        <div class="info-right">
          <!-- TODO: implement cancellation -->
          <div
            v-if="job.status !== 'complete'"
            class="info-right-cancel"
            @click.stop.prevent="clearNotificationById(transferId)"
          >
            Cancel
          </div>
          <div
            v-else
            class="info-right-cancel"
            @click.stop.prevent="clearNotificationById(transferId)"
          >
            Clear
          </div>
        </div>
      </div>
      <div class="progress-bar">
        <div class="background" />
        <div
          class="part finished"
          :style="{ width: fracToPct(finishedBytes / totalBytes) }"
        />
        <div
          class="part failed"
          :style="{ width: fracToPct((completedBytes + failedBytes) / totalBytes) }"
        />
        <div
          class="part success"
          :style="{ width: fracToPct(completedBytes / totalBytes) }"
        />
      </div>
      <div class="action">
        <div class="action-left">
          <div v-if="['counting'].includes(job.status) && job.destination" class="action-detail ">
            <icon-action type="arrow-up" class="action-icon blue" />
            <span>Calculating...</span>
          </div>
          <div v-else-if="['copying'].includes(job.status) && job.destination" class="action-detail">
            <icon-action type="arrow-up" class="action-icon blue" />
            <span>Copying</span>
          </div>
          <div v-else-if="job.status === 'complete' && job.destination" class="action-detail ">
            <icon-action type="check" class="action-icon" />
            <span>Complete</span>
          </div>
        </div>
        <div class="action-right">
          <span>{{ formatBytes(finishedBytes) }}</span>
          <span>of</span>
          <span>{{ formatBytes(totalBytes) }}</span>
        </div>
      </div>
    </div>
    <!-- <div class="close" @click.stop.prevent="clearNotificationById(job.id)">
      <menu-action-icon type="cross" />
    </div> -->
  </div>
</template>

<style lang="sass" scoped>
@import '~/assets/styles/generated/variables.sass'

.container
  @apply flex px-[#{$menu-tray-px-item}] py-[#{$menu-tray-py-item}] gap-[#{$menu-tray-gap-item}] items-center w-full

.preview
  @apply w-7 h-7 flex-none items-center justify-center
.preview-icon
  @apply h-full w-full

.content
  @apply flex flex-col gap-[6px] w-full min-w-0
.info
  @apply flex
.info-left
  @apply flex
.info-right
  @apply flex ml-auto t-menu-xs text-[#{$menu-tray-t-accent}] items-center
.info-right-cancel
  @apply hover:bg-[#{$menu-bg-item-hover}] p-1 -m-1 rounded-[#{$menu-br-item}] cursor-pointer
.progress-bar
  @apply h-[#{$menu-tray-h-progress}]
  @apply relative w-full bg-[#{$menu-tray-bg-progress}] rounded-full overflow-hidden
.part
  @apply absolute top-0 left-0 h-full
  &.failed
    @apply bg-$global-red-primary
  &.finished
    @apply bg-$global-blue-primary
  &.success
    @apply bg-$global-green-primary

.action
  @apply flex items-center
  @apply text-[#{$global-fs-sm}]
  @apply text-[#{$menu-tray-t-status}]
  @apply fw-[450]

.action-left
  @apply flex items-center
.action-detail
  @apply  flex gap-1
.action-right
  @apply flex ml-auto gap-0.75
.action-icon
  @apply rounded-full
  .blue
    @apply text-$global-blue-primary bg-$global-blue-accent
  .green
    @apply text-$global-green-primary bg-$global-green-accent
</style>
